import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate, Routes, Route, Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { useAdminStatus } from '@/hooks/useAdminStatus';

// Dashboards
import { AdminDashboard } from './admin/AdminDashboard';
import { LogisticsDashboard } from './admin/LogisticsDashboard';

// Shared pages
import { AdminPackages } from './admin/AdminPackages';
import { AdminTasks } from './admin/AdminTasks';
import { AdminUsers } from './admin/AdminUsers';
import { AdminTeam } from './admin/AdminTeam';
import { AdminPlaceholder } from './admin/AdminPlaceholder';

const Admin = () => {
  const { user, loading } = useAuth();
  const { isAdmin: hasAdminRole, isManager, loading: roleLoading } = useAdminStatus();
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);
  const [packageCounts, setPackageCounts] = useState({
    pendingReview: 0,
    awaitingPickupConfirmation: 0,
    pickupScheduled: 0,
    pickedUp: 0,
    delivered: 0,
    pendingPickupUsers: 0,
  });

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const checkAccess = async () => {
      if (!user || roleLoading) return;

      // Allow both admins and managers
      if (!hasAdminRole && !isManager) {
        toast.error('Access denied. Admin or Manager privileges required.');
        navigate('/dashboard');
        return;
      }

      setIsAuthorized(true);
      setChecking(false);
      fetchPackageCounts();
    };

    if (user && !roleLoading) {
      checkAccess();
    }
  }, [user, hasAdminRole, isManager, roleLoading, navigate]);

  const fetchPackageCounts = async () => {
    try {
      const { data: packages } = await (supabase as any)
        .from('packages')
        .select('approval_status, status');

      const { data: schedules } = await (supabase as any)
        .from('pickup_schedules')
        .select('status');

      // Count users with pending pickups requiring admin review for bell badge
      const { data: pendingPickups } = await (supabase as any)
        .from('user_pickups')
        .select('user_id')
        .in('status', ['awaiting_confirmation', 'awaiting_admin_confirmation']);

      const pendingPickupUserCount = pendingPickups
        ? new Set(pendingPickups.map((p: any) => p.user_id)).size
        : 0;

      const counts = {
        pendingReview: packages?.filter((p: any) => p.approval_status === 'pending').length || 0,
        awaitingPickupConfirmation: packages?.filter((p: any) => 
          p.approval_status === 'approved' && p.status === 'pending'
        ).length || 0,
        pickupScheduled: schedules?.filter((s: any) => s.status === 'scheduled').length || 0,
        pickedUp: packages?.filter((p: any) => p.status === 'picked_up').length || 0,
        delivered: packages?.filter((p: any) => p.status === 'delivered').length || 0,
        pendingPickupUsers: pendingPickupUserCount,
      };

      setPackageCounts(counts);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error fetching package counts:', error);
      }
    }
  };

  const pendingCount = packageCounts.pendingPickupUsers;

  if (loading || checking || roleLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!isAuthorized) {
    return null;
  }

  // Both admins and managers see the same dashboard
  const DashboardComponent = AdminDashboard;

  return (
    <AdminLayout
      userEmail={user?.email}
      pendingCount={pendingCount}
      packageCounts={packageCounts}
    >
      <Routes>
        <Route index element={<DashboardComponent />} />
        <Route path="logistics" element={<LogisticsDashboard />} />
        <Route path="packages" element={<AdminPackages />} />
        <Route path="tasks" element={<AdminTasks />} />
        <Route path="team" element={<AdminTeam />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="analytics" element={<AdminPlaceholder title="Analytics" description="View reports and insights" />} />
        <Route path="settings" element={<AdminPlaceholder title="Admin Settings" description="Configure admin preferences" />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AdminLayout>
  );
};

export default Admin;
